import { type FC, useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  Filter,
  FilterProvider,
  useFilter,
} from "@/partner/shared/components/Filter";
import { AutomationKindType } from "@/types/graphql/partner.generated";
import { useAutomationGraphRulesQuery } from "@/partner/entities/marketing";
import {
  AUTOMATION_FILTER_STATUSES,
  AUTOMATION_LIST_KINDS,
  AUTOMATION_STATUS_KEYS,
  KIND_KEYS,
} from "../model/automationLabels";
import { AutomationDialog } from "./AutomationDialog";
import { AutomationsList } from "./AutomationsList";
import { automationTabOf, type AutomationTab } from "../model/automationTabs";
import { CreateAutomationDialog } from "./CreateAutomationDialog";
import { preloadAutomationEditor } from "./loadAutomationEditor";
import {
  listRefetchOnClose,
  type AutomationDialogOutcome,
} from "../model/listRefresh";

// AutomationDialog сам грузится статически: только React Flow и его CSS
// остаются в отдельном чанке, через React.lazy внутри AutomationDialog
// (Review I4: одна граница Suspense внутри уже открытого диалога, а не
// диалог поверх диалога).

/**
 * Автоматизации по событиям платформы (спека marketing-automations, фаза
 * 3) и цепочки (фаза 4), устроены как «Email-рассылки»: поиск, статус и
 * вид в панели фильтра,
 * создание коротким диалогом по ?tab=create, редактор графа
 * fullScreen-диалогом по ?tab=edit&id= (Task 21), у цепочки вкладка
 * участников по ?tab=participants&id= в том же диалоге.
 */
const AutomationsInner: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const editId = searchParams.get("id");
  const client = useApolloClient();
  const {
    filter: { status },
  } = useFilter();
  const statusFilter = AUTOMATION_FILTER_STATUSES.find((s) => s === status);

  // Супер-ревью 10: чанк редактора (React Flow) грузится вместе со
  // страницей, а не после ответа AutomationEditorQuery в открытом диалоге.
  // Отказ не страшен: граница ошибки диалога загрузит чанк сама и покажет
  // «Повторить».
  useEffect(() => {
    preloadAutomationEditor().catch(() => undefined);
  }, []);

  // Ре-ревью I1: греем кеш шаблонов создания обоих разделов заранее
  // (cache-first, один раз за сессию), чтобы диалог «Новая автоматизация»
  // открывался сразу с шаблонами, а не подрастал на первом открытии.
  useAutomationGraphRulesQuery({
    variables: { kind: AutomationKindType.PlatformEventType },
  });
  useAutomationGraphRulesQuery({
    variables: { kind: AutomationKindType.JourneyType },
  });

  const openCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };
  const openEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };
  // Созданная автоматизация сразу открывается в редакторе без новой
  // записи в истории: «Назад» не должен снова открывать выбор шаблона.
  const onCreated = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams, { replace: true });
  };
  // Вкладка цепочки меняет только ?tab= (id остаётся) и заменяет запись
  // истории: «Назад» закрывает диалог, а не листает вкладки.
  const onTabChange = (next: AutomationTab) => {
    searchParams.set("tab", next);
    setSearchParams(searchParams, { replace: true });
  };
  const editorTab = automationTabOf(tab);
  const closeDialog = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };
  // Строка списка обновляется ответом мутации в кеше; перечитывается
  // список только тогда, когда смена статуса меняет его состав.
  // Зовётся при размонтировании диалога, в том числе по «Назад» браузера.
  const onEditorSettled = (outcome: AutomationDialogOutcome) => {
    if (listRefetchOnClose(statusFilter, outcome)) {
      void client
        .refetchQueries({ include: ["AutomationFlowsList"] })
        .catch(() => undefined);
    }
  };

  return (
    <>
      <Stack spacing={1}>
        <Filter
          title={t("automations.list.title")}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
              onClick={openCreate}
            >
              {t("common.actions.add")}
            </Button>
          }
          fields={{ search: true }}
          selects={[
            {
              name: "status",
              label: t("automations.filter.status"),
              options: AUTOMATION_FILTER_STATUSES.map((status) => ({
                value: status,
                label: t(AUTOMATION_STATUS_KEYS[status]),
              })),
            },
            {
              name: "kind",
              label: t("automations.filter.kind"),
              options: AUTOMATION_LIST_KINDS.map((kind) => ({
                value: kind,
                label: t(KIND_KEYS[kind]),
              })),
            },
          ]}
        />
        <AutomationsList onEdit={openEdit} onCreate={openCreate} />
      </Stack>
      {tab === "create" && (
        <CreateAutomationDialog onClose={closeDialog} onCreated={onCreated} />
      )}
      {/* Тот же key на обеих вкладках: переключение не пересоздаёт диалог. */}
      {editorTab && editId && (
        <AutomationDialog
          key={editId}
          id={editId}
          onClose={closeDialog}
          onSettled={onEditorSettled}
          tab={editorTab}
          onTabChange={onTabChange}
        />
      )}
    </>
  );
};

export const Automations: FC = () => (
  <FilterProvider>
    <AutomationsInner />
  </FilterProvider>
);
